import { inject, Injectable } from '@angular/core';
import {
  ApiV1Service,
  FileStatus,
  ProvidedEvidenceIntake,
  SaveVerdictRequest,
  ViewService,
} from 'api';
import { filter, map, Observable, tap } from 'rxjs';
import { HttpEventType } from '@angular/common/http';

export interface ConfirmSubmitResult {
  readonly persisted: boolean;
}

export interface UploadEvidenceDocumentResult {
  readonly id?: number;
  readonly evidenceId?: number;
  readonly filename?: string;
  readonly filetype?: string;
  readonly filesize?: number;
  readonly fileStatus?: FileStatus;
}

@Injectable({ providedIn: 'root' })
export class RequirementIntakeCommandService {
  private readonly apiV1Service = inject(ApiV1Service);
  private readonly viewService = inject(ViewService);

  confirmNotApplicable(
    requirementApplicationId: number,
    justification: string,
  ): Observable<ConfirmSubmitResult> {
    return this.apiV1Service
      .markRequirementApplicationNotApplicable(requirementApplicationId, justification)
      .pipe(map(() => ({ persisted: true })));
  }

  confirmApplicable(requirementApplicationId: number): Observable<ConfirmSubmitResult> {
    return this.apiV1Service
      .markRequirementApplicationApplicable(requirementApplicationId)
      .pipe(map(() => ({ persisted: true })));
  }

  uploadEvidenceDocument(
    evidenceId: number,
    file: File,
    percentageCallback: (percentage: number) => void,
  ): Observable<UploadEvidenceDocumentResult> {
    return this.apiV1Service.uploadDocument(file, evidenceId, 'events', true).pipe(
      tap((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          const total = event.total ?? file.size;
          percentageCallback(Math.round((100 * event.loaded) / total));
        }
      }),
      filter((event) => event.type === HttpEventType.Response),
      map((event) => event.body!),
    );
  }

  deleteEvidenceDocument(fileAttachmentId: number): Observable<ConfirmSubmitResult> {
    return this.apiV1Service
      .deleteDocument(fileAttachmentId)
      .pipe(map(() => ({ persisted: true })));
  }

  saveVerdict(
    requirementApplicationId: number,
    command: SaveVerdictRequest,
  ): Observable<ConfirmSubmitResult> {
    return this.apiV1Service
      .saveVerdict(requirementApplicationId, command)
      .pipe(map(() => ({ persisted: true })));
  }

  createOtherEvidence(requirementApplicationId: number): Observable<ProvidedEvidenceIntake> {
    return this.viewService.createOtherEvidenceForRequirementApplication(requirementApplicationId);
  }

  markEvidenceNotProvided(evidenceId: number): Observable<ConfirmSubmitResult> {
    return this.apiV1Service
      .markEvidenceAsNotProvided(evidenceId)
      .pipe(map(() => ({ persisted: true })));
  }
}
